import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    Link as LinkIcon,
    QrCode as QrCodeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { ltiApi } from '../../services/api/lti.api';
import { useAuthStore } from '../../stores';

const GenerateLinkDialog = ({ open, onClose, assignment }) => {
    const { t } = useTranslation();
    const { token } = useAuthStore();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generatedLink, setGeneratedLink] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Form data
    const [externalSystemId, setExternalSystemId] = useState(null);
    const [expiresAt, setExpiresAt] = useState('');
    const [maxUses, setMaxUses] = useState('');
    const [metadata, setMetadata] = useState('');

    // Loaded data
    const [externalSystems, setExternalSystems] = useState([]);

    useEffect(() => {
        if (open) {
            loadExternalSystems();
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, assignment]);

    const loadExternalSystems = async () => {
        try {
            const response = await ltiApi.getExternalSystems(token);
            // Handle different response formats
            if (Array.isArray(response)) {
                setExternalSystems(response);
            } else if (response?.results && Array.isArray(response.results)) {
                setExternalSystems(response.results);
            } else {
                setExternalSystems([]);
            }
        } catch (error) {
            setExternalSystems([]);
        }
    };

    const resetForm = () => {
        setExternalSystemId(null);
        setExpiresAt('');
        setMaxUses('');
        setMetadata('');
        setGeneratedLink(null);
        setError(null);
        setCopySuccess(false);
    };

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);

        try {
            let parsedMetadata = {};
            if (metadata && metadata.trim()) {
                try {
                    parsedMetadata = JSON.parse(metadata);
                } catch (parseError) {
                    setError(t('generate_link.errors.invalid_json'));
                    setLoading(false);
                    return;
                }
            }

            const payload = {
                assignment_id: assignment.id,
                external_system_id: externalSystemId || null,
                expires_at: expiresAt || null,
                max_uses: maxUses ? parseInt(maxUses) : null,
                metadata: parsedMetadata
            };

            const response = await ltiApi.createAssignmentLink(
                assignment.id,
                payload,
                token
            );

            setGeneratedLink(response);
        } catch (error) {
            setError(
                error.message ||
                t('generate_link.errors.generation_failed')
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (generatedLink?.launch_url) {
            navigator.clipboard.writeText(generatedLink.launch_url);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleDownloadQR = () => {
        const canvas = document.getElementById('qr-code-canvas');
        if (canvas) {
            const url = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `assignment-${assignment.id}-qr.png`;
            link.href = url;
            link.click();
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon color="primary" />
                        <Typography variant="h6">
                            {t('generate_link.title')}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {!generatedLink ? (
                    <>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            {t('generate_link.info')}
                        </Alert>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                disabled
                                label={t('generate_link.fields.assignment')}
                                value={assignment?.title || ''}
                                variant="outlined"
                            />

                            <FormControl fullWidth>
                                <InputLabel>{t('generate_link.fields.external_system')}</InputLabel>
                                <Select
                                    value={externalSystemId || ''}
                                    onChange={(e) => setExternalSystemId(e.target.value || null)}
                                    label={t('generate_link.fields.external_system')}
                                >
                                    <MenuItem value="">
                                        <em>{t('generate_link.any_system')}</em>
                                    </MenuItem>
                                    {externalSystems.map(system => (
                                        <MenuItem key={system.id} value={system.id}>
                                            {system.name} ({system.system_type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                type="datetime-local"
                                label={t('generate_link.fields.expires_at')}
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText={t('generate_link.hints.expires_at')}
                            />

                            <TextField
                                fullWidth
                                type="number"
                                label={t('generate_link.fields.max_uses')}
                                value={maxUses}
                                onChange={(e) => setMaxUses(e.target.value)}
                                inputProps={{ min: 1 }}
                                helperText={t('generate_link.hints.max_uses')}
                            />

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={t('generate_link.fields.metadata')}
                                value={metadata}
                                onChange={(e) => setMetadata(e.target.value)}
                                placeholder='{"course_id": "CS101", "section": "A"}'
                                helperText={t('generate_link.hints.metadata')}
                            />

                            {error && (
                                <Alert severity="error" onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Alert severity="success">
                            {t('generate_link.success')}
                        </Alert>

                        {/* Link Section */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('generate_link.generated_link')}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: 1,
                                        wordBreak: 'break-all',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {generatedLink.launch_url}
                                </Typography>
                                <Tooltip title={copySuccess ? t('common.copied') : t('common.copy')}>
                                    <IconButton
                                        onClick={handleCopyLink}
                                        size="small"
                                        color={copySuccess ? 'success' : 'default'}
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>

                        {/* QR Code Section */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <QrCodeIcon color="primary" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t('generate_link.qr_code')}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadQR}
                                >
                                    {t('common.download')}
                                </Button>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: 2,
                                bgcolor: 'white',
                                borderRadius: 1
                            }}>
                                <QRCodeCanvas
                                    id="qr-code-canvas"
                                    value={generatedLink.launch_url}
                                    size={200}
                                    level="H"
                                    includeMargin={true}
                                />
                            </Box>
                        </Paper>

                        <Divider />

                        {/* Link Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('generate_link.link_details')}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {generatedLink.expires_at && (
                                    <Chip
                                        label={`${t('generate_link.expires')}: ${new Date(generatedLink.expires_at).toLocaleString()}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {generatedLink.max_uses && (
                                    <Chip
                                        label={`${t('generate_link.max_uses')}: ${generatedLink.max_uses}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                <Chip
                                    label={`${t('generate_link.uses')}: ${generatedLink.current_uses || 0}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>

                            {generatedLink.external_system_name && (
                                <Typography variant="caption" color="text.secondary">
                                    {t('generate_link.external_system')}: {generatedLink.external_system_name}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {!generatedLink ? (
                    <>
                        <Button onClick={handleClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                        >
                            {t('generate_link.generate')}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" onClick={handleClose}>
                        {t('common.done')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default GenerateLinkDialog;
